/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import styled from "@emotion/styled";
import { Panel, Spinner, SpinnerSize } from '@fluentui/react';
import { Fragment, useContext, useImperativeHandle, useState } from "react";
import { ApplyButton } from "./ApplyButton";
import { ColumnList } from "./ColumnList";
import { ColumnOptionsContext } from "./ColumnOptions";
import { SelectAllCheckbox } from "./SelectAllCheckbox";
import { ViewContextButton } from "./ViewContextButton";
import { ViewDescription } from "./ViewDescription";
import { ViewDialog } from "./ViewDialog";
import { ViewSelect } from "./ViewSelect";
import * as UIConfig from "../../config/ui";

export type ViewDialogState = {
  isOpen: boolean;
  userAppSettingId: number | null;
};

const initStateViewDialog = {
  isOpen: false,
  userAppSettingId: null
};

export const ColumnOptionsPanel = () => {
  const [viewDialog, setViewDialog] = useState<ViewDialogState>(
    initStateViewDialog
  );
  const {
    isReady,
    selectedView,
    closePanel,
    renderAdditionalOptions,
    strings,
    additionalOptionsHeight,
    additionalOptionsStyle,
    disableButtons,
    columnOptionsRef
  } = useContext(ColumnOptionsContext);

  useImperativeHandle(columnOptionsRef, () => ({
    closeModal: () => {
      setViewDialog({
        isOpen: false,
        userAppSettingId: null
      });
    }
  }));

  const shouldShowDescription =
    selectedView &&
    typeof selectedView.userAppSettingDescription === "string" &&
    selectedView.userAppSettingDescription.length > 0;

  return (
    <StyledPanel
      isOpen
      onDismiss={closePanel}
      isLightDismiss
      headerText={strings.headerLabel}
      isFooterAtBottom
      onRenderFooterContent={() => (
        <Footer>
          <ApplyButton />
        </Footer>
      )}
      layerProps={{ eventBubblingEnabled: true }}
      className="column-options-panel"
    >
      {!isReady ? (
        <Spinner
          size={SpinnerSize.large}
          css={css`
            position: relative;
            top: 45%;
            transform: translate(0, -50%);
          `}
        />
      ) : (
        <Fragment>
          <ViewContainer>
            <ViewSelect
              css={css`
                flex: 1;
                margin-right: 5px;
              `}
            />
            <ViewContextButton
              setViewDialog={setViewDialog}
              disableButtons={disableButtons}
            />
          </ViewContainer>
          <DescriptionContainer>
            {shouldShowDescription && (
              <ViewDescription
                css={css`
                  margin-right: 5px;
                `}
              />
            )}
            <SelectAllCheckbox
              css={css`
                margin-left: auto;
                margin-top: auto;
                margin-bottom: auto;
                align-self: flex-end;
              `}
            />
          </DescriptionContainer>
          <ColumnList />
          <div
            css={css`
              height: ${additionalOptionsHeight}px;
              padding: 0 24px;
              overflow-y: auto;
              ${additionalOptionsStyle
                ? { ...additionalOptionsStyle.container }
                : ""};
            `}
          >
            {renderAdditionalOptions && renderAdditionalOptions()}
          </div>
          {viewDialog.isOpen && (
            <ViewDialog
              userAppSettingId={viewDialog.userAppSettingId}
              close={() => setViewDialog(initStateViewDialog)}
            />
          )}
        </Fragment>
      )}
    </StyledPanel>
  );
};

const StyledPanel = styled(Panel)`
  top: ${UIConfig.headerHeight}px;
  height: calc(100vh - ${UIConfig.headerHeight}px);
  .ms-Panel-content {
    padding: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  .ms-Panel-scrollableContent {
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .ms-Panel-commands {
    height: ${UIConfig.columnOptionsHeaderHeight}px;
    display: flex;
    align-items: center;
    margin-top: 0;
    box-sizing: border-box;
  }
  .ms-Panel-footer {
    height: ${UIConfig.columnOptionsFooterHeight}px;
    box-sizing: border-box;
  }
  .ms-Panel-footerInner {
    padding: 10px 24px;
  }
  .ms-Panel-navigation {
    width: 100%;
  }
  box-sizing: border-box;
`;

const DescriptionContainer = styled.div`
  display: flex;
  align-items: center;
  height: ${UIConfig.columnOptionsDescriptionContainerHeight}px;
  padding: 0 24px;
  gap: 5px;
`;

const ViewContainer = styled.div`
  display: flex;
  align-items: center;
  height: ${UIConfig.columnOptionsViewSelectContainer}px;
  box-sizing: border-box;
  padding: 0 24px;
  padding-bottom: 10px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
